import { useHandler } from "@redotech/react-util/hook";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoTextareaInputField,
  RedoTextareaInputFieldSize,
} from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { memo, useEffect, useState } from "react";

/**
 * Blur-commit text input: committing per keystroke re-renders the inspector
 * and steals focus, so local state buffers edits until blur.
 */
export const HsaTextField = memo(function HsaTextField({
  label,
  value,
  onCommit,
  placeholder,
  description,
}: {
  label: string;
  value: string | undefined;
  onCommit: (value: string) => void;
  placeholder?: string;
  description?: string;
}) {
  const [localValue, setLocalValue] = useState(value ?? "");

  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  const handleBlur = useHandler(() => {
    if (localValue !== (value ?? "")) {
      onCommit(localValue);
    }
  });

  return (
    <RedoTextInput
      description={description}
      label={label}
      onBlur={handleBlur}
      placeholder={placeholder}
      setValue={setLocalValue}
      value={localValue}
    />
  );
});

/** Blur-commit number input; commits only when the text parses as a number. */
export const HsaNumberField = memo(function HsaNumberField({
  label,
  value,
  onCommit,
  placeholder,
}: {
  label?: string;
  value: number | undefined;
  onCommit: (value: number) => void;
  placeholder?: string;
}) {
  const [localValue, setLocalValue] = useState(
    value === undefined ? "" : String(value),
  );

  useEffect(() => {
    setLocalValue(value === undefined ? "" : String(value));
  }, [value]);

  const handleBlur = useHandler(() => {
    const parsed = Number(localValue);
    if (localValue !== "" && !Number.isNaN(parsed) && parsed !== value) {
      onCommit(parsed);
    }
  });

  return (
    <RedoTextInput
      inputTypeHint="number"
      label={label}
      onBlur={handleBlur}
      placeholder={placeholder}
      setValue={setLocalValue}
      value={localValue}
    />
  );
});

/** Blur-commit textarea, for content-screen paragraphs. */
export const HsaTextareaField = memo(function HsaTextareaField({
  label,
  value,
  onCommit,
  placeholder,
}: {
  label?: string;
  value: string;
  onCommit: (value: string) => void;
  placeholder?: string;
}) {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleBlur = useHandler(() => {
    if (localValue !== value) {
      onCommit(localValue);
    }
  });

  return (
    <RedoTextareaInputField
      label={label}
      onBlur={handleBlur}
      placeholder={placeholder}
      setValue={setLocalValue}
      size={RedoTextareaInputFieldSize.SMALL}
      value={localValue}
    />
  );
});
